/// <reference lib="webworker" />

import { BaseNotification } from '@fc-core/services/notification-payload';

declare const clients: Clients;

export const urlMap = {
  vehicle: (id: string) => `/vehicles/main/vehicles/${id}/details/`,
  map: (id: string) => `/map/detail/${id}/info`,
  location: (id: string) => `/locations/main/location-list/${id}/`,
  chat: (id: string) => `/chats/direct-message/${id}/`,
};

export const handleBackgroundMessageClick = async (event: any) => {
  event.notification.close();

  const { ['link_type']: linkType, ['link_id']: linkId } =
    event.notification.data;

  const url = urlMap[linkType as keyof typeof urlMap](linkId);

  event.waitUntil(
    clients
      .matchAll({ type: 'window', includeUncontrolled: true })
      .then((clientList) => {
        // If a window/tab for this origin is already open, focus it.
        for (const client of clientList) {
          if ('focus' in client) {
            // If any window is open, just open the new one alongside.
            // Focusing an existing arbitrary window might be confusing.
            break; // Found an open window, proceed to open new one.
          }
        }
        // If no window is open or we decided not to reuse, open a new one.
        if (clients.openWindow) {
          return clients.openWindow(url);
        }
      }),
  );
};

export const handleForegroundMessageClick = async (event: any) => {
  if (!event.notification.data) return;
  const { ['link_type']: linkType, ['link_id']: linkId } =
    event.notification.data;

  const targetUrl = urlMap[linkType as keyof typeof urlMap](linkId);
  await window.open(targetUrl, '_blank');
};

export const DISMISS_ACTION = 'dismiss';

export const buildChatGroupKey = (channelId: string): string =>
  `chat:${channelId}`;

export const isDismissalPayload = (payload: BaseNotification): boolean =>
  payload.data?.action === DISMISS_ACTION;

type DeliveredNotification = Notification & { timestamp?: number };

const wasSentBefore = (
  notification: DeliveredNotification,
  cutoffMs: number,
): boolean => {
  if (Number.isNaN(cutoffMs)) return true;

  const sentAt = notification.data?.sent_at
    ? Date.parse(notification.data.sent_at)
    : notification.timestamp;

  return sentAt === undefined || Number.isNaN(sentAt) || sentAt <= cutoffMs;
};

export const closeNotificationsByGroup = async (
  serviceWorkerRegistration: ServiceWorkerRegistration,
  groupKey: string,
  cutoffMs = NaN,
  notificationId?: string,
): Promise<void> => {
  const openNotifications = await serviceWorkerRegistration.getNotifications();
  openNotifications
    .filter((notification) =>
      notificationId
        ? notification.data?.notification_id === notificationId
        : notification.data?.group_key === groupKey &&
          wasSentBefore(notification, cutoffMs),
    )
    .forEach((notification) => notification.close());
};

export const closeDismissedNotifications = async (
  serviceWorkerRegistration: ServiceWorkerRegistration,
  payload: BaseNotification,
): Promise<void> => {
  const groupKey = payload.data.group_key;
  if (!groupKey) return;

  const cutoffMs = payload.data.cutoff ? Date.parse(payload.data.cutoff) : NaN;
  await closeNotificationsByGroup(
    serviceWorkerRegistration,
    groupKey,
    cutoffMs,
    payload.data.notification_id,
  );
};

export const showNotification = async (
  serviceWorkerRegistration: ServiceWorkerRegistration,
  payload: BaseNotification,
): Promise<void> => {
  const notificationId = payload.data.notification_id;
  const notificationOptions: NotificationOptions = {
    body: payload.data.body,
    icon: payload.data.image,
    badge: payload.data.badge,
    data: payload.data,
    ...(notificationId ? { tag: notificationId } : {}),
  };
  await serviceWorkerRegistration.showNotification(
    payload.data.title,
    notificationOptions,
  );
};

export const handlePushPayload = async (
  serviceWorkerRegistration: ServiceWorkerRegistration,
  payload: BaseNotification,
): Promise<void> => {
  if (isDismissalPayload(payload)) {
    await closeDismissedNotifications(serviceWorkerRegistration, payload);
    return;
  }
  await showNotification(serviceWorkerRegistration, payload);
};
